import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, User, Star, CreditCard, GitBranch,
    Edit3, Save, MapPin, Phone, Mail, Globe, BadgeCheck,
    Crown, Zap, TrendingUp, Eye, LogOut, ChevronRight,
    AlertCircle, CheckCircle, Clock, Shield, ShieldCheck,
    ThumbsUp, MessageSquare, ArrowRight, Copy, ExternalLink,
    Briefcase, FileText, Hammer, Users, Send, X
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import { HomlyLogo } from '../../components/brand/HomlyLogo';

const API = process.env.REACT_APP_BACKEND_URL;

function getProToken() { return localStorage.getItem('pro_token') || localStorage.getItem('token'); }

async function proFetchV1(path, options = {}) {
    const token = getProToken();
    return fetch(`${API}/api/professionals${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
    });
}

async function proFetchV2(path, options = {}) {
    const token = getProToken();
    return fetch(`${API}/api/professionals/v2${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
    });
}

function StarRating({ rating, size = 16, interactive, onChange }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    className={`cursor-${interactive ? 'pointer' : 'default'} transition-colors ${
                        i <= (hover || Math.round(rating)) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'
                    }`}
                    size={size}
                    onMouseEnter={() => interactive && setHover(i)}
                    onMouseLeave={() => interactive && setHover(0)}
                    onClick={() => interactive && onChange?.(i)}
                />
            ))}
        </div>
    );
}

// ===================== TRUST SCORE GAUGE =====================
function TrustScoreGauge({ score, label }) {
    const radius = 54;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 80 ? '#22C55E' : score >= 60 ? '#7C3AED' : score >= 40 ? '#F59E0B' : '#EF4444';

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-32 h-32">
                <svg className="transform -rotate-90 w-32 h-32">
                    <circle cx="64" cy="64" r={radius} stroke="currentColor" strokeWidth="8" fill="none" className="text-muted/20" />
                    <motion.circle
                        cx="64" cy="64" r={radius} stroke={color} strokeWidth="8" fill="none"
                        strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference}
                        animate={{ strokeDashoffset: offset }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-foreground">{score}</span>
                    <span className="text-[10px] text-muted-foreground">/100</span>
                </div>
            </div>
            <span className="text-sm font-semibold mt-2" style={{ color }}>{label}</span>
        </div>
    );
}

// ===================== BADGE COMPONENT =====================
function BadgeIcon({ badge }) {
    const icons = { ShieldCheck, Shield, Zap, ThumbsUp, Crown };
    const Icon = icons[badge.icon] || ShieldCheck;
    return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card" title={badge.label}>
            <Icon className="w-3.5 h-3.5" style={{ color: badge.color }} />
            <span className="text-xs font-medium text-foreground">{badge.label}</span>
        </div>
    );
}

// ===================== PIPELINE STATUS CONFIG =====================
const PIPELINE_CONFIG = {
    new: { label: 'Nouveau', color: '#7C3AED', icon: Users, bg: 'bg-[#7C3AED]/10' },
    contacted: { label: 'Contact', color: '#3B82F6', icon: Phone, bg: 'bg-blue-500/10' },
    quote_sent: { label: 'Devis envoyé', color: '#F59E0B', icon: FileText, bg: 'bg-[#F59E0B]/10' },
    quote_accepted: { label: 'Devis accepté', color: '#22C55E', icon: CheckCircle, bg: 'bg-green-500/10' },
    in_progress: { label: 'Chantier', color: '#EF4444', icon: Hammer, bg: 'bg-red-500/10' },
    completed: { label: 'Terminé', color: '#10B981', icon: CheckCircle, bg: 'bg-emerald-500/10' },
    review_requested: { label: 'Avis demandé', color: '#8B5CF6', icon: MessageSquare, bg: 'bg-violet-500/10' },
    reviewed: { label: 'Avis publié', color: '#22D3EE', icon: Star, bg: 'bg-cyan-500/10' },
    dispute: { label: 'Litige', color: '#EF4444', icon: AlertCircle, bg: 'bg-red-500/10' },
};

const NEXT_STATUS = {
    new: 'contacted',
    contacted: 'quote_sent',
    quote_sent: 'quote_accepted',
    quote_accepted: 'in_progress',
    in_progress: 'completed',
    completed: 'review_requested',
};

// ===================== OVERVIEW TAB =====================
function OverviewTab({ data }) {
    if (!data) return null;
    const { profile, pipeline, trust_score, badges, review_stats, profile_completeness, subscription } = data;
    const isPremium = subscription?.plan === 'premium';

    return (
        <div className="space-y-6">
            {/* Vue synthétique — 5 bouquets KPI contextuels (P3) */}
            <div data-testid="pro-bouquets-strip">
                <KPIBouquetsStrip scope="mine" variant="agent" />
            </div>

            {/* Welcome + Trust Score */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-card border-border p-5 md:col-span-2 overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-[80px] bg-[#7C3AED]/8" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-xl">{profile?.company_name?.charAt(0)}</span>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-foreground truncate">{profile?.company_name}</h2>
                            <p className="text-sm text-muted-foreground">{profile?.name}</p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {badges?.map(b => <BadgeIcon key={b.id} badge={b} />)}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="bg-card border-border p-5 flex items-center justify-center">
                    <TrustScoreGauge score={trust_score?.score || 0} label={trust_score?.label || 'N/A'} />
                </Card>
            </div>

            {/* Pipeline mini */}
            <Card className="bg-card border-border p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-[#7C3AED]" /> Pipeline
                    </h3>
                    <span className="text-xs text-muted-foreground">{pipeline?.total || 0} contacts total</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {['new', 'contacted', 'quote_sent', 'in_progress', 'completed'].map(s => {
                        const cfg = PIPELINE_CONFIG[s];
                        const count = pipeline?.stats?.[s] || 0;
                        return (
                            <div key={s} className={`${cfg.bg} rounded-xl p-3 text-center`}>
                                <p className="text-2xl font-bold" style={{ color: cfg.color }}>{count}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{cfg.label}</p>
                            </div>
                        );
                    })}
                </div>

                {/* Urgent actions */}
                {(pipeline?.urgent?.new_requests > 0 || pipeline?.urgent?.pending_reviews > 0) && (
                    <div className="mt-4 flex gap-3 flex-wrap">
                        {pipeline.urgent.new_requests > 0 && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20">
                                <Users className="w-4 h-4 text-[#7C3AED]" />
                                <span className="text-xs text-foreground font-medium">{pipeline.urgent.new_requests} nouvelle(s) demande(s)</span>
                            </div>
                        )}
                        {pipeline.urgent.pending_reviews > 0 && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                                <MessageSquare className="w-4 h-4 text-[#F59E0B]" />
                                <span className="text-xs text-foreground font-medium">{pipeline.urgent.pending_reviews} avis en attente</span>
                            </div>
                        )}
                    </div>
                )}
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: 'Note moyenne', value: review_stats?.avg_rating?.toFixed(1) || '0', icon: Star, color: 'text-yellow-400' },
                    { label: 'Avis V2', value: review_stats?.total_reviews || 0, icon: MessageSquare, color: 'text-[#7C3AED]' },
                    { label: 'Taux reco.', value: `${review_stats?.recommendation_rate || 0}%`, icon: ThumbsUp, color: 'text-[#F59E0B]' },
                    { label: 'Profil', value: `${profile_completeness}%`, icon: User, color: 'text-green-400' },
                ].map((stat, i) => (
                    <Card key={i} className="bg-card border-border p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <stat.icon className={`w-4 h-4 ${stat.color}`} />
                            <span className="text-[11px] text-muted-foreground">{stat.label}</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    </Card>
                ))}
            </div>

            {/* Completeness CTA */}
            {profile_completeness < 100 && (
                <Card className="bg-[#F59E0B]/5 border-[#F59E0B]/20 p-4">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-[#F59E0B] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">Complétez votre profil ({profile_completeness}%)</p>
                            <p className="text-xs text-muted-foreground">Un profil complet inspire 3x plus confiance</p>
                        </div>
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden shrink-0">
                            <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] rounded-full" style={{ width: `${profile_completeness}%` }} />
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
}

// ===================== PIPELINE TAB =====================
function PipelineTab({ data, onRefresh }) {
    const [contacts, setContacts] = useState([]);
    const [pipelineStats, setPipelineStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [reviewLink, setReviewLink] = useState(null);

    const fetchPipeline = useCallback(async () => {
        try {
            const url = filter ? `/contacts/pipeline?status=${filter}` : '/contacts/pipeline';
            const res = await proFetchV2(url);
            if (res.ok) {
                const d = await res.json();
                setContacts(d.contacts);
                setPipelineStats(d.pipeline_stats);
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [filter]);

    useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

    const advanceStatus = async (contactId, newStatus) => {
        setActionLoading(contactId);
        try {
            const res = await proFetchV2(`/contacts/${contactId}/status`, {
                method: 'PUT', body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) { fetchPipeline(); onRefresh?.(); }
        } catch (e) { console.error(e); }
        setActionLoading(null);
    };

    const requestReview = async (contactId) => {
        setActionLoading(contactId);
        try {
            const res = await proFetchV2(`/contacts/${contactId}/request-review`, { method: 'POST' });
            if (res.ok) {
                const d = await res.json();
                setReviewLink(`${window.location.origin}/pro/avis/${d.review_token}`);
                fetchPipeline(); onRefresh?.();
            }
        } catch (e) { console.error(e); }
        setActionLoading(null);
    };

    const copyLink = (link) => {
        navigator.clipboard.writeText(link);
    };

    if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6">
            {/* Review link modal */}
            {reviewLink && (
                <Card className="bg-green-500/10 border-green-500/20 p-4">
                    <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">Lien d'avis généré</p>
                            <p className="text-xs text-muted-foreground mb-2">Envoyez ce lien au client pour qu'il dépose son avis vérifié :</p>
                            <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                                <code className="text-xs text-foreground flex-1 truncate">{reviewLink}</code>
                                <Button variant="ghost" size="sm" onClick={() => copyLink(reviewLink)} data-testid="copy-review-link"><Copy className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setReviewLink(null)}><X className="w-4 h-4" /></Button>
                    </div>
                </Card>
            )}

            {/* Pipeline summary bar */}
            <div className="flex gap-1.5 overflow-x-auto pb-2">
                <button
                    onClick={() => setFilter(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        !filter ? 'bg-[#7C3AED] text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="pipeline-filter-all"
                >Tous ({Object.values(pipelineStats).reduce((a, b) => a + b, 0)})</button>
                {Object.entries(PIPELINE_CONFIG).map(([key, cfg]) => {
                    const count = pipelineStats[key] || 0;
                    if (count === 0 && key !== 'new') return null;
                    return (
                        <button
                            key={key}
                            onClick={() => setFilter(key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                                filter === key ? 'text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'
                            }`}
                            style={filter === key ? { backgroundColor: cfg.color } : {}}
                            data-testid={`pipeline-filter-${key}`}
                        >{cfg.label} ({count})</button>
                    );
                })}
            </div>

            {/* Contact cards */}
            <div className="space-y-3">
                {contacts.map(contact => {
                    const cfg = PIPELINE_CONFIG[contact.status] || PIPELINE_CONFIG.new;
                    const StatusIcon = cfg.icon;
                    const nextStatus = NEXT_STATUS[contact.status];
                    const nextCfg = nextStatus ? PIPELINE_CONFIG[nextStatus] : null;
                    const isLoading = actionLoading === contact.id;
                    const canRequestReview = ['contacted', 'quote_sent', 'quote_accepted', 'in_progress', 'completed'].includes(contact.status);

                    return (
                        <Card key={contact.id} className="bg-card border-border p-4" data-testid={`pipeline-contact-${contact.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center shrink-0`}>
                                        <StatusIcon className="w-5 h-5" style={{ color: cfg.color }} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-foreground truncate">{contact.client_name || 'Client'}</p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {contact.project_description || contact.project_type}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                            <Badge className="border-0 text-[10px]" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                                                {cfg.label}
                                            </Badge>
                                            {contact.agent_name && (
                                                <span className="text-[10px] text-muted-foreground">via {contact.agent_name}</span>
                                            )}
                                            <span className="text-[10px] text-muted-foreground">
                                                {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {nextStatus && contact.status !== 'reviewed' && contact.status !== 'dispute' && (
                                        <Button
                                            size="sm" variant="outline"
                                            className="text-xs gap-1"
                                            onClick={() => advanceStatus(contact.id, nextStatus)}
                                            disabled={isLoading}
                                            data-testid={`advance-${contact.id}`}
                                        >
                                            {isLoading ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                                            {nextCfg?.label}
                                        </Button>
                                    )}
                                    {canRequestReview && contact.status !== 'review_requested' && contact.status !== 'reviewed' && (
                                        <Button
                                            size="sm"
                                            className="text-xs bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 gap-1"
                                            onClick={() => requestReview(contact.id)}
                                            disabled={isLoading}
                                            data-testid={`request-review-${contact.id}`}
                                        >
                                            <Send className="w-3 h-3" /> Avis
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Contact info */}
                            {(contact.client_email || contact.client_phone) && (
                                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                                    {contact.client_email && (
                                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                            <Mail className="w-3 h-3" /> {contact.client_email}
                                        </span>
                                    )}
                                    {contact.client_phone && (
                                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                            <Phone className="w-3 h-3" /> {contact.client_phone}
                                        </span>
                                    )}
                                </div>
                            )}
                        </Card>
                    );
                })}

                {contacts.length === 0 && (
                    <div className="text-center py-16">
                        <GitBranch className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm">Aucun contact dans le pipeline</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Les agents immobiliers vous enverront des contacts via Homly</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ===================== REVIEWS V2 TAB =====================
function ReviewsV2Tab({ data }) {
    if (!data) return null;
    const { reviews, review_stats } = data;

    const verificationLabels = {
        contact_verified: { label: 'Contact vérifié', color: '#3B82F6' },
        quote_verified: { label: 'Devis vérifié', color: '#F59E0B' },
        job_completed: { label: 'Chantier réalisé', color: '#22C55E' },
    };

    return (
        <div className="space-y-6">
            {/* Stats */}
            <Card className="bg-card border-border p-6">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="text-center">
                        <p className="text-5xl font-bold text-foreground">{review_stats?.avg_rating?.toFixed(1) || '0'}</p>
                        <StarRating rating={review_stats?.avg_rating || 0} size={20} />
                        <p className="text-sm text-muted-foreground mt-1">{review_stats?.total_reviews || 0} avis vérifiés</p>
                    </div>
                    <div className="flex-1 space-y-3 w-full">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-3 rounded-lg bg-blue-500/10">
                                <p className="text-lg font-bold text-blue-400">{review_stats?.contact_reviews || 0}</p>
                                <p className="text-[10px] text-muted-foreground">Contact</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-[#F59E0B]/10">
                                <p className="text-lg font-bold text-[#F59E0B]">{review_stats?.quote_reviews || 0}</p>
                                <p className="text-[10px] text-muted-foreground">Devis</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-green-500/10">
                                <p className="text-lg font-bold text-green-400">{review_stats?.job_reviews || 0}</p>
                                <p className="text-[10px] text-muted-foreground">Chantier</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <ThumbsUp className="w-4 h-4 text-[#F59E0B]" />
                            <span className="text-sm text-foreground font-medium">{review_stats?.recommendation_rate || 0}% recommandent</span>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Reviews list */}
            <div className="space-y-3">
                {reviews?.map(review => {
                    const verif = verificationLabels[review.verification_level] || verificationLabels.contact_verified;
                    return (
                        <Card key={review.id} className="bg-card border-border p-4" data-testid={`review-${review.id}`}>
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center text-white text-xs font-bold">
                                        {review.reviewer_name?.charAt(0) || 'C'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">{review.reviewer_name}</p>
                                        <div className="flex items-center gap-2">
                                            <Badge className="border-0 text-[9px] px-1.5 py-0" style={{ backgroundColor: `${verif.color}15`, color: verif.color }}>
                                                <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> {verif.label}
                                            </Badge>
                                            {review.source === 'agent_recommendation' && review.agent_name && (
                                                <span className="text-[10px] text-muted-foreground">via {review.agent_name}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <StarRating rating={review.overall_rating} size={14} />
                            </div>

                            {review.comment && (
                                <p className="text-sm text-muted-foreground leading-relaxed mt-2">{review.comment}</p>
                            )}

                            {/* Sub ratings */}
                            {review.sub_ratings && Object.keys(review.sub_ratings).length > 0 && (
                                <div className="flex gap-3 mt-3 flex-wrap">
                                    {Object.entries(review.sub_ratings).map(([key, val]) => (
                                        <span key={key} className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                            {key.replace(/_/g, ' ')}: {val}/5
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Pro response */}
                            {review.pro_response && (
                                <div className="mt-3 pl-4 border-l-2 border-[#7C3AED]/30">
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground">Votre réponse :</span> {review.pro_response.text}
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                                <span>{new Date(review.created_at).toLocaleDateString('fr-FR')}</span>
                                {review.recommends && <span className="text-green-400 flex items-center gap-0.5"><ThumbsUp className="w-2.5 h-2.5" /> Recommande</span>}
                            </div>
                        </Card>
                    );
                })}

                {(!reviews || reviews.length === 0) && (
                    <div className="text-center py-16">
                        <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm">Aucun avis vérifié pour le moment</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Faites avancer vos contacts dans le pipeline pour générer des avis</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ===================== PROFILE TAB =====================
function ProfileTab({ data, onUpdate }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (data?.profile) setForm(data.profile); }, [data]);

    const update = (field, value) => setForm(f => ({ ...f, [field]: value }));
    const updateAddr = (field, value) => setForm(f => ({ ...f, address: { ...f.address, [field]: value } }));

    const save = async () => {
        setSaving(true);
        try {
            const res = await proFetchV1('/auth/profile', {
                method: 'PUT',
                body: JSON.stringify({
                    name: form.name, company_name: form.company_name, phone: form.phone,
                    description: form.description, website: form.website, siret: form.siret,
                    service_radius_km: form.service_radius_km, address: form.address,
                }),
            });
            if (res.ok) { setEditing(false); onUpdate(); }
        } catch (e) { console.error(e); }
        setSaving(false);
    };

    if (!data?.profile) return null;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-foreground">Mon profil</h2>
                {editing ? (
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditing(false); setForm(data.profile); }}>Annuler</Button>
                        <Button size="sm" onClick={save} disabled={saving} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white">
                            <Save className="w-3 h-3 mr-1" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                        <Edit3 className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                )}
            </div>

            <Card className="bg-card border-border p-6">
                <h3 className="font-semibold text-foreground mb-4 text-sm flex items-center gap-2">
                    <User className="w-4 h-4 text-[#7C3AED]" /> Identité
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { label: 'Nom complet', field: 'name' },
                        { label: 'Entreprise', field: 'company_name' },
                        { label: 'Téléphone', field: 'phone' },
                        { label: 'SIRET', field: 'siret' },
                        { label: 'Site web', field: 'website' },
                        { label: "Rayon d'intervention (km)", field: 'service_radius_km', type: 'number' },
                    ].map(({ label, field, type }) => (
                        <div key={field}>
                            <label className="text-xs text-muted-foreground">{label}</label>
                            {editing
                                ? <Input type={type || 'text'} value={form[field] || ''} onChange={e => update(field, type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)} />
                                : <p className="text-sm text-foreground mt-1">{form[field] || 'Non renseigné'}</p>
                            }
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="bg-card border-border p-6">
                <h3 className="font-semibold text-foreground mb-4 text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#F59E0B]" /> Localisation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { label: 'Ville', field: 'city' },
                        { label: 'Code postal', field: 'postal_code' },
                        { label: 'Département', field: 'department' },
                    ].map(({ label, field }) => (
                        <div key={field}>
                            <label className="text-xs text-muted-foreground">{label}</label>
                            {editing
                                ? <Input value={form.address?.[field] || ''} onChange={e => updateAddr(field, e.target.value)} />
                                : <p className="text-sm text-foreground mt-1">{form.address?.[field] || ''}</p>
                            }
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="bg-card border-border p-6">
                <h3 className="font-semibold text-foreground mb-4 text-sm">Description</h3>
                {editing ? (
                    <textarea value={form.description || ''} onChange={e => update('description', e.target.value)}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-3 text-sm text-foreground min-h-[120px]" />
                ) : (
                    <p className="text-sm text-muted-foreground leading-relaxed">{form.description || 'Aucune description'}</p>
                )}
            </Card>
        </div>
    );
}

// ===================== SUBSCRIPTION TAB =====================
function SubscriptionTab({ data }) {
    if (!data) return null;
    const { subscription } = data;
    const isPremium = subscription?.plan === 'premium';

    return (
        <div className="space-y-6">
            <Card className={`overflow-hidden ${isPremium ? 'border-[#F59E0B]/30' : 'border-border'}`}>
                <div className={`h-1.5 bg-gradient-to-r ${isPremium ? 'from-[#7C3AED] to-[#F59E0B]' : 'from-[#7C3AED] to-[#7C3AED]'}`} />
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            {isPremium ? <Crown className="w-6 h-6 text-[#F59E0B]" /> : <Zap className="w-6 h-6 text-[#7C3AED]" />}
                            <div>
                                <h3 className="font-bold text-foreground text-lg">Forfait Partenaire</h3>
                                <p className="text-sm text-muted-foreground">Statut : {subscription?.status}</p>
                            </div>
                        </div>
                        <p className="text-3xl font-extrabold text-foreground">
                            29<span className="text-lg">€</span><span className="text-sm font-normal text-muted-foreground"> HT/mois</span>
                        </p>
                    </div>
                </div>
            </Card>

            <Card className="bg-card border-border p-6">
                <h3 className="font-bold text-foreground mb-4 text-sm">Tout est inclus dans votre forfait</h3>
                <div className="space-y-2">
                    {[
                        "Profil dans l'annuaire",
                        'Badge "Vérifié Homly"',
                        'Pipeline CRM',
                        'Avis vérifiés illimités',
                        'Mise en avant dans les résultats',
                        'Score de confiance public',
                        'Statistiques avancées',
                        'Contact email + téléphone direct',
                        'Support prioritaire',
                    ].map((feature, i) => (
                        <div key={i} className={`flex items-center gap-3 py-2 ${i > 0 ? 'border-t border-border' : ''}`}>
                            <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                            <span className="text-sm text-foreground">{feature}</span>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

// ===================== MAIN DASHBOARD =====================
export default function ProDashboardPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [dashData, setDashData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchDashboard = useCallback(async () => {
        try {
            const res = await proFetchV2('/dashboard');
            if (res.ok) {
                const data = await res.json();
                setDashData(data);
            } else if (res.status === 401 || res.status === 403) {
                localStorage.removeItem('pro_token');
                navigate('/pro/login');
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [navigate]);

    useEffect(() => {
        const token = getProToken();
        if (!token) { navigate('/pro/login'); return; }
        fetchDashboard();
    }, [navigate, fetchDashboard]);

    const logout = () => {
        localStorage.removeItem('pro_token');
        localStorage.removeItem('pro_profile');
        navigate('/pro/login');
    };

    const tabs = [
        { id: 'overview', label: 'Accueil', icon: LayoutDashboard },
        { id: 'pipeline', label: 'Pipeline', icon: GitBranch },
        { id: 'reviews', label: 'Avis', icon: Star },
        { id: 'profile', label: 'Profil', icon: User },
        { id: 'subscription', label: 'Forfait', icon: CreditCard },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="pro-dashboard">
            {/* Header */}
            <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <HomlyLogo size="md" />
                        <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 text-[10px]">PRO</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground hidden md:block">{dashData?.profile?.email}</span>
                        <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground" data-testid="pro-logout">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
                {/* Tabs */}
                <div className="flex gap-1 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                    {tabs.map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                data-testid={`pro-tab-${tab.id}`}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                                    isActive
                                        ? 'bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white shadow-lg'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                    <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                        {activeTab === 'overview' && <OverviewTab data={dashData} />}
                        {activeTab === 'pipeline' && <PipelineTab data={dashData} onRefresh={fetchDashboard} />}
                        {activeTab === 'reviews' && <ReviewsV2Tab data={dashData} />}
                        {activeTab === 'profile' && <ProfileTab data={dashData} onUpdate={fetchDashboard} />}
                        {activeTab === 'subscription' && <SubscriptionTab data={dashData} />}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
